import { useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import type { TaskUpdateBodyStatus } from '#/shared/api/generated/shipment/models'
import { useUpdateItemApiV1ToolbarTypeItemIdPatch } from '#/shared/api/generated/shipment/theCargoShipment'
import { getApiErrorDetail } from '#/shared/lib/api/error-handler'
import { toast } from '#/shared/ui/sonner'

import { TOOLBAR_KIND, TOOLBAR_QUERY_KEY_PREFIX } from './toolbar-kinds'

export const TASK_STATUS = {
  pending: 'pending',
  completed: 'completed',
  archived: 'archived',
} as const satisfies Record<string, NonNullable<TaskUpdateBodyStatus>>

export function useUpdateToolbarTask() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const mutation = useUpdateItemApiV1ToolbarTypeItemIdPatch({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          predicate: (query) => {
            const head = query.queryKey[0]
            return (
              typeof head === 'string' &&
              head.startsWith(TOOLBAR_QUERY_KEY_PREFIX)
            )
          },
        })
        toast.success(t('shipmentToolbar.update.success'))
      },
      onError: (error) => {
        toast.error(
          getApiErrorDetail(error) ?? t('shipmentToolbar.update.error'),
        )
      },
    },
  })

  const updateStatus = useCallback(
    (taskId: string, status: NonNullable<TaskUpdateBodyStatus>) =>
      mutation.mutateAsync({
        type: TOOLBAR_KIND.task,
        itemId: taskId,
        data: { kind: TOOLBAR_KIND.task, status },
      }),
    [mutation],
  )

  const markCompleted = useCallback(
    (taskId: string) => updateStatus(taskId, TASK_STATUS.completed),
    [updateStatus],
  )

  return {
    updateStatus,
    markCompleted,
    isPending: mutation.isPending,
    pendingTaskId: mutation.isPending ? mutation.variables.itemId : undefined,
  }
}
